import React from 'react';
import PropTypes from 'prop-types';

import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import DmarcChart from './dmarc-chart';
import DmarcSelectorContainer from '../../containers/domain-portfolio/domain-portfolio-dmarc-selector-container';
import DmarcChartLegend from './dmarc-chart-legend';
import DownloadMenuContainer from '../../containers/download-menu-container';
import Grid from '@material-ui/core/Grid';
import LoadingSpinner from '../../../common/components/loading-spinner';
import Typography from 'fuse-ui/components/Typography';
import { withStyles } from '@material-ui/core/styles';

const styles = theme => ( {
    bodyText: {
        marginBottom: theme.spacing.unit,
    },
    cardRoot: {
        '@media print': {
            border: 'none'
        },
        marginTop: theme.spacing.unit * 2,
    },
    chartWrapper: {
        'padding-left': theme.spacing.unit * 2
    },
    domainPortfolioPrint: {
        '@media print': {
            padding: `0 ${theme.spacing.unit * 2}px`
        }
    },
    gridContent: {
        height: '100%',
        paddingBottom: 200,
        position: 'relative',
    },
    legend: {
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'flex-end',
        position: 'absolute',
    },
    portfolioContent: {
        height: '100%'
    },
    summaryStat: {
        'align-items': 'baseline',
        display: 'flex',
        'justify-content': 'left',
    },
    summaryStatPct: {
        'padding-left': theme.spacing.unit
    },
    summaryStatTitle: {
        'padding-bottom': theme.spacing.unit
    },
    summaryStatWrapper: {
        'padding-bottom': theme.spacing.unit * 2
    }
} );

const formatPercentage = ( count, total ) => {
    let pct = 0;
    if( count && total ){
        pct = count / total;
    }

    let percentageOpts = { maximumFractionDigits: 0, minimumFractionDigits: 0 };
    return ( pct * 100 ).toLocaleString( undefined, percentageOpts );
};

const DmarcChartSection = ( { classes, isLoading, domainsPolicyCountsData, aggDomainsData } ) => {
    let claimedDomainCount = aggDomainsData.length;
    let { reject, quarantine, none, no_record: noRecord } = domainsPolicyCountsData;

    let rejectPercentage = formatPercentage( reject, claimedDomainCount );
    let quarantinePercentage = formatPercentage( quarantine, claimedDomainCount );
    let nonePercentage = formatPercentage( none, claimedDomainCount );
    let noRecordPercentage = formatPercentage( noRecord, claimedDomainCount );

    return (
        <Card className={classes.cardRoot}>
            {
                isLoading ?
                    <LoadingSpinner /> : (
                        <div id="domain-portfolio" className={classes.domainPortfolioPrint}>
                            <DownloadMenuContainer chartName="domain-portfolio" />
                            <DmarcSelectorContainer />
                            <CardHeader
                                title={(
                                    <React.Fragment>
                                        <Typography variant="h6">
                            DMARC Pass Rate and Policies
                                        </Typography>
                                        <Typography color="textSecondary" variant="subtitle1">
                            Emails sent to consumer mailbox providers
                                        </Typography>
                                    </React.Fragment>
                                )}
                            />
                            <CardContent className={`${classes.portfolioContent}`}>
                                <Grid container justify="center">
                                    <Grid item xs={12} sm={4}>
                                        <div className={classes.gridContent}>
                                            <Grid container >
                                                <Grid className={classes.summaryStatTitle} item xs={12} >
                                                    <Typography variant="subtitle1">
                                                    Total {claimedDomainCount.toLocaleString()} Claimed Domains
                                                    </Typography>
                                                </Grid>

                                                <Grid item xs={6} className={classes.summaryStatWrapper}>
                                                    <Typography variant="overline" color="textSecondary" >
                                                    Domains at p=reject
                                                    </Typography>
                                                    <span className={classes.summaryStat}>
                                                        <Typography variant="h4" color="textPrimary" display="inline">
                                                            {reject.toLocaleString()}
                                                        </Typography>
                                                        <Typography className={classes.summaryStatPct} variant="caption" color="textSecondary" display="inline"> {rejectPercentage}% </Typography>
                                                    </span>
                                                </Grid>

                                                <Grid item xs={6} className={classes.summaryStatWrapper}>
                                                    <Typography variant="overline" color="textSecondary">
                                                        Domains at p=quarantine
                                                    </Typography>
                                                    <span className={classes.summaryStat}>
                                                        <Typography variant="h4" color="textPrimary">
                                                            {quarantine.toLocaleString()}
                                                        </Typography>
                                                        <Typography className={classes.summaryStatPct} variant="caption" color="textSecondary"> {quarantinePercentage}% </Typography>
                                                    </span>
                                                </Grid>

                                                <Grid item xs={6} className={classes.summaryStatWrapper}>
                                                    <Typography variant="overline" color="textSecondary">
                                                    Domains at p=none
                                                    </Typography>
                                                    <span className={classes.summaryStat}>
                                                        <Typography variant="h4" color="textPrimary">
                                                            {none.toLocaleString()}
                                                        </Typography>
                                                        <Typography className={classes.summaryStatPct} variant="caption" color="textSecondary"> {nonePercentage}% </Typography>
                                                    </span>
                                                </Grid>
                                                <Grid item xs={6} className={classes.summaryStatWrapper}>
                                                    <Typography variant="overline" color="textSecondary">
                                                    Domains have no DMARC record
                                                    </Typography>
                                                    <span className={classes.summaryStat}>
                                                        <Typography variant="h4" color="textPrimary">
                                                            {noRecord.toLocaleString()}
                                                        </Typography>
                                                        <Typography className={classes.summaryStatPct} variant="caption" color="textSecondary"> {noRecordPercentage}% </Typography>
                                                    </span>
                                                </Grid>
                                            </Grid>

                                            <div className={classes.legend}>
                                                <div>
                                                    <Typography variant="overline" color="textSecondary">
                                            Interpreting This Graph
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary"  className={classes.bodyText}>
                                            You’re working to get all domains to the top right corner.
                                            If you intend to use both SPF and DKIM to pass DMARC,
                                            view the chart with the filter set to DMARC Double Pass.
                                                    </Typography>
                                                </div>
                                                <DmarcChartLegend aggDomainsData={aggDomainsData}/>
                                            </div>

                                        </div>
                                    </Grid>
                                    <Grid item xs={12} sm={8} className={classes.chartWrapper} >
                                        {
                                            aggDomainsData &&
                                            <DmarcChart aggDomainsData={aggDomainsData} />
                                        }
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </div>
                    )}
        </Card>
    );
};

DmarcChartSection.propTypes = {
    aggDomainsData: PropTypes.array,
    classes: PropTypes.object,
    domainsPolicyCountsData: PropTypes.object,
    isLoading: PropTypes.bool,
};

export default withStyles( styles )( DmarcChartSection );
